import { useState } from 'react';
import { Laptop, Smartphone, Trash2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Button } from '@/components/button';
import { Badge } from '@/components/badge';
import { Dialog } from '@/components/dialog';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import {
  useMySessions,
  useRevokeOtherSessions,
  useRevokeSession,
} from '@/lib/sessions';
import { useToast } from '@/components/toast';
import type { SessionData } from '@/types/api';

export function SessionsTab() {
  const { data, isLoading } = useMySessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const toast = useToast();
  const [confirmOthers, setConfirmOthers] = useState(false);
  const others = (data ?? []).filter((s) => !s.is_current).length;

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Active sessions</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Browsers and devices currently signed in to your account.
          </p>
        </div>
        {others > 0 ? (
          <Button variant="secondary" onClick={() => setConfirmOthers(true)}>
            <Trash2 className="size-3.5" /> Sign out {others} other{others === 1 ? '' : 's'}
          </Button>
        ) : null}
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <SkeletonList rows={3} />
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={Laptop}
            title="No sessions"
            description="You'll see active devices here once you sign in."
          />
        ) : (
          <DataList>
            {data.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                onRevoke={async () => {
                  await revoke.mutateAsync(s.id);
                  toast.success('Session revoked');
                }}
              />
            ))}
          </DataList>
        )}
      </CardBody>

      <Dialog
        open={confirmOthers}
        onClose={() => setConfirmOthers(false)}
        title="Sign out other sessions?"
      >
        <p className="text-sm text-muted-foreground">
          Every browser signed in as you (except this one) will be signed out immediately. They'll
          need to log in again.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmOthers(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={revokeOthers.isPending}
            onClick={async () => {
              await revokeOthers.mutateAsync();
              toast.success('Other sessions signed out');
              setConfirmOthers(false);
            }}
          >
            {revokeOthers.isPending ? 'Signing out…' : 'Sign out others'}
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}

function SessionRow({
  session,
  onRevoke,
}: {
  session: SessionData;
  onRevoke: () => Promise<void>;
}) {
  const Icon = session.os === 'iOS' || session.os === 'Android' ? Smartphone : Laptop;
  return (
    <DataListRow interactive={false}>
      <div className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {session.browser ?? 'Unknown browser'} · {session.os ?? 'Unknown OS'}
          </span>
          {session.is_current ? <Badge tone="success">This device</Badge> : null}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {session.ip_address ?? 'Unknown IP'} · Last active {relativeWhen(session.last_active_at)}
        </div>
      </div>
      {!session.is_current ? (
        <Button variant="secondary" onClick={onRevoke}>
          <Trash2 className="size-3.5" /> Revoke
        </Button>
      ) : null}
    </DataListRow>
  );
}

function relativeWhen(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return date.toLocaleDateString();
}
